import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import GlobalCurrencySelector from './GlobalCurrencySelector';
import { useUserAuth } from '../contexts/UserAuthContext';
import { apiConfig } from '../config/api';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [lightLogo, setLightLogo] = useState('/images/logo.png');
  const [darkLogo, setDarkLogo] = useState('/images/logo.png');
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef(null);
  const location = useLocation();
  const { isAuthenticated, logout } = useUserAuth();

  // Check for dark mode
  useEffect(() => {
    const checkDarkMode = () => {
      const darkMode = localStorage.getItem('darkMode') === 'true' || 
                       document.documentElement.classList.contains('dark') ||
                       window.matchMedia('(prefers-color-scheme: dark)').matches;
      setIsDarkMode(darkMode);
    };
    
    checkDarkMode();
    // Listen for dark mode changes
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });
    
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', checkDarkMode);
    
    return () => {
      observer.disconnect();
      window.matchMedia('(prefers-color-scheme: dark)').removeEventListener('change', checkDarkMode);
    };
  }, []);

  // Fetch logo images from page management
  useEffect(() => {
    const fetchLogoImages = async () => {
      try {
        const response = await fetch(`${apiConfig.baseURL}/api/pages/home?section=hero`);
        const data = await response.json();
        
        if (data.success && data.data?.hero) {
          const heroContent = data.data.hero;
          if (heroContent.lightImage?.content) {
            setLightLogo(heroContent.lightImage.content);
          }
          if (heroContent.darkImage?.content) {
            setDarkLogo(heroContent.darkImage.content);
          }
        }
      } catch (error) {
        console.error('Error fetching logo images:', error);
        // Keep default logos on error
      }
    };
    
    fetchLogoImages();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = () => {
    logout();
    setIsOpen(false);
  };

  const navItems = [
    { 
      name: 'What We Do', 
      path: '/what-we-do',
      submenu: [
        { name: 'Our Programs', path: '/what-we-do' },
        { name: 'Our Team', path: '/what-we-do/team' }
      ]
    },
    { name: 'Our Initiatives', path: '/initiatives' },
    { name: 'Events', path: '/events' },
    { name: 'Gallery', path: '/gallery' },
    { name: 'Sponsor Child', path: '/sponsor-child' },
    { name: 'Widow Support', path: '/widow-application' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className="fixed top-0 left-0 right-0 bg-white shadow-sm border-b border-gray-100 transition-all duration-300 w-full max-w-full"
      style={{ zIndex: 50 }}
    >
      <div className="container-custom max-w-full px-4 sm:px-6 lg:px-8 relative" style={{ overflow: 'visible' }}>
        <div className="flex items-center justify-between h-16 lg:h-20 w-full" style={{ overflow: 'visible' }}>
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <div className="w-16 h-12 rounded-lg overflow-hidden">
              <img 
                src={isDarkMode ? darkLogo : lightLogo} 
                alt="Abraham Oladotun Foundation Logo" 
                className="w-full h-full object-contain"
                onError={(e) => {
                  // Fallback to default logo if image fails to load
                  e.target.src = '/images/logo.png';
                }}
              />
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8 relative z-10" style={{ overflow: 'visible' }}>
            {navItems.map((item) => (
              item.submenu ? (
                <div
                  key={item.name}
                  ref={item.name === 'What We Do' ? dropdownRef : null}
                  className="relative"
                  style={{ overflow: 'visible' }}
                  onMouseEnter={(e) => {
                    if (item.name === 'What We Do' && dropdownRef.current) {
                      const rect = dropdownRef.current.getBoundingClientRect();
                      setDropdownPosition({
                        top: rect.bottom + 4,
                        left: rect.left
                      });
                    }
                    setActiveDropdown(item.name);
                  }}
                  onMouseLeave={(e) => {
                    // Don't close immediately - allow mouse to move to dropdown
                    // Check if mouse is moving towards dropdown
                    const relatedTarget = e.relatedTarget;
                    const dropdownElement = document.querySelector('[data-dropdown-menu]');
                    if (!relatedTarget || (dropdownElement && dropdownElement.contains(relatedTarget))) {
                      // Mouse is moving to dropdown or already in dropdown
                      return;
                    }
                    // Small delay to allow reaching dropdown
                    setTimeout(() => {
                      const stillHovering = dropdownElement && (
                        dropdownElement.matches(':hover') || 
                        dropdownRef.current?.matches(':hover')
                      );
                      if (!stillHovering) {
                        setActiveDropdown(null);
                      }
                    }, 150);
                  }}
                >
                  <Link
                    to={item.path}
                    className={`font-medium transition-colors duration-200 flex items-center ${
                      location.pathname === item.path || location.pathname.startsWith(item.path + '/')
                        ? 'text-primary-600'
                        : 'text-gray-800 hover:text-primary-600'
                    }`}
                  >
                    {item.name}
                    <i className="fas fa-chevron-down ml-1 text-xs"></i>
                  </Link>
                  {activeDropdown === item.name && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      data-dropdown-menu
                      style={{
                        position: 'fixed',
                        zIndex: 50,
                        top: `${dropdownPosition.top}px`,
                        left: `${dropdownPosition.left}px`
                      }}
                      className="w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2"
                      onMouseEnter={() => {
                        setActiveDropdown(item.name);
                      }}
                      onMouseLeave={(e) => {
                        // Don't close if mouse is moving back to trigger
                        const relatedTarget = e.relatedTarget;
                        if (!relatedTarget || !dropdownRef.current?.contains(relatedTarget)) {
                          setActiveDropdown(null);
                        }
                      }}
                    >
                      {item.submenu.map((subItem) => (
                        <Link
                          key={subItem.name}
                          to={subItem.path}
                          onClick={() => setActiveDropdown(null)}
                          className={`block px-4 py-2 text-sm transition-colors duration-200 ${
                            location.pathname === subItem.path
                              ? 'text-primary-600 bg-primary-50'
                              : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
                          }`}
                        >
                          {subItem.name}
                        </Link>
                      ))}
                    </motion.div>
                  )}
                </div>
              ) : (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`font-medium transition-colors duration-200 ${
                    location.pathname === item.path
                      ? 'text-primary-600'
                      : 'text-gray-800 hover:text-primary-600'
                  }`}
                >
                  {item.name}
                </Link>
              )
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center space-x-4">
            {isAuthenticated() ? (
              // User is logged in - show user menu
              <div className="flex items-center space-x-3">
                <Link
                  to="/dashboard"
                  className="text-sm text-gray-600 hover:text-primary-600 transition-colors"
                >
                  <i className="fas fa-tachometer-alt mr-1"></i>
                  Dashboard
                </Link>
                <button
                  onClick={handleLogout}
                  className="btn-outline text-sm"
                >
                  Logout
                </button>
              </div>
            ) : (
              // User is not logged in - show login/signup buttons
              <div className="flex items-center space-x-3">
                <Link
                  to="/login"
                  className="btn-outline text-sm"
                >
                  Login
                </Link>
                <Link
                  to="/signup"
                  className="btn-primary text-sm"
                >
                  Sign Up
                </Link>
              </div>
            )}
            <div className="flex items-center space-x-3">
              <GlobalCurrencySelector />
              <Link
                to="/donate"
                className="btn-primary text-sm"
              >
                Donate Now
              </Link>
            </div>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 rounded-md text-gray-700 hover:text-primary-600 hover:bg-gray-100"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              {isOpen ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden bg-white shadow-lg rounded-lg mt-2 overflow-hidden w-full max-w-full"
            >
              <div className="px-4 py-2 space-y-1 w-full max-w-full">
                {navItems.map((item) => (
                  <div key={item.name}>
                    <Link
                      to={item.path}
                      onClick={() => setIsOpen(false)}
                      className={`block px-3 py-2 rounded-md text-base font-medium transition-colors duration-200 ${
                        location.pathname === item.path || (item.submenu && location.pathname.startsWith(item.path + '/'))
                          ? 'text-primary-600 bg-primary-50'
                          : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
                      }`}
                    >
                      {item.name}
                    </Link>
                    {item.submenu && (
                      <div className="ml-4 mt-1 space-y-1">
                        {item.submenu.map((subItem) => (
                          <Link
                            key={subItem.name}
                            to={subItem.path}
                            onClick={() => setIsOpen(false)}
                            className={`block px-3 py-2 rounded-md text-sm transition-colors duration-200 ${
                              location.pathname === subItem.path
                                ? 'text-primary-600 bg-primary-50'
                                : 'text-gray-600 hover:text-primary-600 hover:bg-gray-50'
                            }`}
                          >
                            {subItem.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                <div className="pt-4 space-y-2">
                  {isAuthenticated() ? (
                    // User is logged in - show user info and logout
                    <div className="space-y-2">
                      <Link
                        to="/dashboard"
                        className="block w-full text-center btn-primary text-sm mb-2"
                      >
                        <i className="fas fa-tachometer-alt mr-1"></i>
                        Dashboard
                      </Link>
                      <button
                        onClick={handleLogout}
                        className="block w-full text-center btn-outline text-sm"
                      >
                        Logout
                      </button>
                    </div>
                  ) : (
                    // User is not logged in - show login/signup buttons
                    <div className="space-y-2">
                      <Link
                        to="/login"
                        onClick={() => setIsOpen(false)}
                        className="block w-full text-center btn-outline text-sm"
                      >
                        Login
                      </Link>
                      <Link
                        to="/signup"
                        onClick={() => setIsOpen(false)}
                        className="block w-full text-center btn-primary text-sm"
                      >
                        Sign Up
                      </Link>
                    </div>
                  )}
                  <div className="space-y-3">
                    <GlobalCurrencySelector className="w-full" />
                    <Link
                      to="/donate"
                      onClick={() => setIsOpen(false)}
                      className="block w-full text-center btn-primary text-sm"
                    >
                      Donate Now
                    </Link>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.nav>
  );
};

export default Navbar;
